'use client';

import { useMergeRefs } from '@floating-ui/react';
import { forwardRef, useContext } from 'react';

import { css, cx } from '@/styled-system/css';

import { TableHeaderCellContext } from './context/table-header-cell.context';
import { TableRootContext } from './context/table-root.context';
import { TableRowContext } from './context/table-row.context';
import { useSortableHeaderCell } from './hooks/use-sortable-header-cell';
import { tableCellSharedRecipe } from './styles/table-cell-shared.styles';
import { tableCellRecipe } from './styles/table-cell.styles';
import { tableHeaderCellRecipe } from './styles/table-header-cell.styles';
import type { TableHeaderCellPropsInterface } from './types/table-header-cell.types';

/**
 * A native [`<th>`](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/th) element.
 *
 * @since 8.0.0
 */
export const TableHeaderCell = forwardRef<HTMLTableCellElement, TableHeaderCellPropsInterface>(
  (props: TableHeaderCellPropsInterface, forwardedRef) => {
    const { appearance, size } = useContext(TableRootContext);
    const { parent, sticky } = useContext(TableHeaderCellContext);
    const { interactive, selected } = useContext(TableRowContext);

    const {
      align: alignProp,
      children,
      className,
      css: cssProp,
      onClick,
      onKeyDown,
      scope,
      sortDirectionOrder,
      value,
      ...rest
    } = props;

    const {
      align,
      context: conditionalContext,
      props: conditionalProps,
      ref: headerCellRef,
      sortable,
    } = useSortableHeaderCell({
      align: alignProp,
      children,
      onClick,
      onKeyDown,
      scope,
      sortDirectionOrder,
      value,
    });

    const styles = css(
      tableCellSharedRecipe.raw({
        align,
        appearance,
        size,
      }),
      parent === 'body'
        ? tableCellRecipe.raw({ interactive, selected })
        : tableHeaderCellRecipe.raw({ appearance, interactive: sortable, size, sticky }),
      cssProp,
    );

    const refs = useMergeRefs([headerCellRef, forwardedRef]);

    return (
      <TableHeaderCellContext.Provider value={conditionalContext}>
        <th
          ref={refs}
          data-c11n-version={__C11N_VERSION__}
          data-c11n-component="Table.HeaderCell"
          className={cx('c11n', 'group', styles, className)}
          {...conditionalProps}
          {...rest}
        />
      </TableHeaderCellContext.Provider>
    );
  },
);

TableHeaderCell.displayName = 'Table.HeaderCell';
